import React, { useEffect, useRef } from 'react';
import { Message, MessageSenderType, MessageChannel } from '../../types/support';
import { cn, formatTime, formatDateTime, getUserDisplayName } from '../../utils/helpers';
import Avatar from '../ui/Avatar';
import Spinner from '../ui/Spinner';
import { Lock, Bot } from 'lucide-react';

interface SupportMessageListProps {
  messages: Message[];
  loading: boolean;
}

const SupportMessageList: React.FC<SupportMessageListProps> = ({
  messages,
  loading,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessageCount = useRef(messages.length);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > prevMessageCount.current && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.createdAt).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {} as Record<string, Message[]>);

  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <p className="text-sm">No messages yet</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-6"
    >
      {Object.entries(groupedMessages).map(([date, dateMessages]) => (
        <div key={date}>
          {/* Date separator */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground font-medium">
              {formatDateLabel(date)}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Messages for this date */}
          <div className="space-y-4">
            {dateMessages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Format date label
function formatDateLabel(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// Individual message bubble
interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUser = message.senderType === MessageSenderType.USER;
  const isAgent = message.senderType === MessageSenderType.AGENT;
  const isSystem = message.senderType === MessageSenderType.SYSTEM;
  const isInternal = message.channel === MessageChannel.INTERNAL_NOTE;

  // Internal note styling
  if (isInternal) {
    return (
      <div className="flex justify-center px-4">
        <div className="message-internal max-w-lg px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-3 w-3 text-warning" />
            <span className="text-xs font-medium text-warning">
              Internal Note
            </span>
            <span className="text-xs text-muted-foreground">
              by {getUserDisplayName(message.author)}
            </span>
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {message.body}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {formatTime(message.createdAt)}
          </p>
        </div>
      </div>
    );
  }

  // System message styling
  if (isSystem) {
    return (
      <div className="flex justify-center px-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-background/50 px-3 py-1.5 rounded-full">
          <Bot className="h-3 w-3" />
          <span>{message.body}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex gap-3 max-w-[85%]',
        isAgent ? 'ml-auto flex-row-reverse' : 'mr-auto'
      )}
    >
      <Avatar
        name={isUser ? 'Customer' : getUserDisplayName(message.author)}
        size="sm"
        className="flex-shrink-0 mt-1"
      />
      <div
        className={cn(
          'flex flex-col',
          isAgent ? 'items-end' : 'items-start'
        )}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-foreground">
            {isUser ? 'Customer' : getUserDisplayName(message.author)}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatTime(message.createdAt)}
          </span>
        </div>
        <div className={cn(isUser ? 'message-user' : 'message-agent', 'px-4 py-3')}>
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
            {message.body}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportMessageList;
